import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { dbFiles } from '../../App';
import { UserContext } from '../../universal/context/UserContext';
import { useLoader } from '../../universal/context/LoaderContext';
import { useNotification } from '../../universal/context/NotificationContext';
import * as mammoth from 'mammoth';
import TurndownService from 'turndown';
import { marked } from 'marked';
import htmlDocx from 'html-docx-js/dist/html-docx';
import { saveAs } from 'file-saver';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createPortal } from 'react-dom';
import JSZip from 'jszip';

// Define Prompt shape for dropdown
interface Prompt {
  id: string;
  name: string;
  text: string;
  category?: string;
}

// Define Automation shape
interface Automation {
  id: string;
  status: 'Pending' | 'Processing' | 'Completed' | 'Failed';
  deployer: string;
  date: string;
  promptName: string;
  promptText?: string;
  fileName: string;
  txtFileName?: string;
  createdAt?: number;
  knowledgeUsed?: string[];
}

const AutomationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();

  // Load prompts for dropdown
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [showConvertPopup, setShowConvertPopup] = useState(false);
  const [convertFiles, setConvertFiles] = useState<File[]>([]);
  const [convertPromptId, setConvertPromptId] = useState('');
  const [convertPromptContent, setConvertPromptContent] = useState('');
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  const [customPromptText, setCustomPromptText] = useState('');
  const [hoveredPromptId, setHoveredPromptId] = useState<string | null>(null);
  const [tooltipTarget, setTooltipTarget] = useState<HTMLElement | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPayloadSent, setIsPayloadSent] = useState(false);
  const [convertedMarkdown, setConvertedMarkdown] = useState('');
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [convertFile, setConvertFile] = useState<File | null>(null);

  // State for prompt categories
  const [promptCategories, setPromptCategories] = useState<{ id: string; name: string }[]>([]);
  const [selectedPromptCategory, setSelectedPromptCategory] = useState('');
  const [filteredPrompts, setFilteredPrompts] = useState<Prompt[]>([]);

  // State for Knowledge feature
  const [addKnowledge, setAddKnowledge] = useState(false);
  const [knowledgeFiles, setKnowledgeFiles] = useState<File[]>([]);

  // Add extended prompt state
  const [extendedPromptText, setExtendedPromptText] = useState('');
  const [showExtendedPromptPopup, setShowExtendedPromptPopup] = useState(false);
  const [showViewPromptPopup, setShowViewPromptPopup] = useState(false);

  const [isLoadingAutomations, setIsLoadingAutomations] = useState(true); // New loading state

  // Multi-select state for batch actions
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // State & handlers for mass delete confirmation
  const [showMassDeleteConfirm, setShowMassDeleteConfirm] = useState(false);

  const toggleSelection = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    // fetch prompts to populate selector
    fetchPrompts();
    fetchAutomations();
    fetchPromptCategories(); // Fetch prompt categories
  }, []);

  const fetchAutomations = async () => {
    setIsLoadingAutomations(true); // Set loading to true at the start
    try {
      // Query automations for the current user
      const automationsQuery = query(
        collection(dbFiles, 'automations'),
        where('deployer', '==', user?.name || '')
      );
      const snapshot = await getDocs(automationsQuery);
      // Read saved automations (with createdAt from data)
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...(d.data() as Omit<Automation, 'id'>)
      }));
      // Sort by createdAt descending
      const sortedData = data.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setAutomations(sortedData as Automation[]);
    } catch (error) {
      console.error(error);
      showNotification('Failed to load automations!', 'error');
    } finally {
      setIsLoadingAutomations(false); // Set loading to false at the end
    }
  };

  const fetchPrompts = async () => {
    showLoader();
    try {
      const colRef = collection(dbFiles, 'prompts');
      const snapshot = await getDocs(colRef);
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...(d.data() as Omit<Prompt, 'id'>)
      }));
      setPrompts(data);
    } catch (error) {
      console.error(error);
      showNotification('Failed to load prompts!', 'error');
    } finally {
      hideLoader();
    }
  };

  const fetchPromptCategories = async () => {
    // Not showing loader here as prompts fetch already does
    const categoriesCollectionRef = collection(dbFiles, 'promptCategories');
    try {
      const querySnapshot = await getDocs(categoriesCollectionRef);
      const fetchedCategories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as { name: string })
      }));
      setPromptCategories(fetchedCategories);
    } catch (error) {
      console.error("Error fetching prompt categories:", error);
      // Optionally show a non-blocking notification or log
    }
  };

  const handleSendConversion = async () => {
    if (convertFiles.length === 0 || !convertPromptContent) {
      showNotification('Please select at least one file and a prompt!', 'error');
      return;
    }
    setIsPayloadSent(true);
    showLoader();
    let allSuccessful = true;

    try {
      const promptNameForFile = useCustomPrompt
        ? 'Custom Prompt'
        : prompts.find(p => p.id === convertPromptId)?.name || 'Unknown Prompt';
      let knowledgeContextString = '';

      if (addKnowledge && knowledgeFiles.length > 0) {
        const knowledgeEntriesFormatted = [];
        for (const kFile of knowledgeFiles) {
          try {
            const arrayBuffer = await kFile.arrayBuffer();
            const htmlResult = await mammoth.convertToHtml({ arrayBuffer }, {
              convertImage: mammoth.images.imgElement(image =>
                image.read("base64").then(imageBuffer => ({
                  src: "data:" + image.contentType + ";base64," + imageBuffer
                }))
              )
            });
            // Replace emoji image tags with alt text (only for single-char emoji alt)
            const cleanedHtml = htmlResult.value.replace(/<img[^>]*alt="([^\"]+)"[^>]*>/g, (_match, alt) => {
              // strip only if alt is likely a single emoji (<=2 code units)
              return alt.length <= 2 ? alt : _match;
            });
            const turndownService = new TurndownService();
            const markdownText = turndownService.turndown(cleanedHtml);
            // Construct each entry as a string
            const entryString = `[
Name of File: ${kFile.name}
Content: ${markdownText}
]`;
            knowledgeEntriesFormatted.push(entryString);
          } catch (e) {
            console.error(`Error processing knowledge file ${kFile.name}:`, e);
            showNotification(`Could not process knowledge file: ${kFile.name}`, 'error');
          }
        }
        if (knowledgeEntriesFormatted.length > 0) {
          // Join the string entries with a comma and newline
          knowledgeContextString = `Knowledge: {${knowledgeEntriesFormatted.join(',\n')}}`;
        }
      }

      for (const file of convertFiles) {
        let currentAutomationId: string | null = null; // To store ID for potential deletion
        try {
          const arrayBuffer = await file.arrayBuffer();
          const htmlResult = await mammoth.convertToHtml({ arrayBuffer }, {
            convertImage: mammoth.images.imgElement(image =>
              image.read("base64").then(imageBuffer => ({
                src: "data:" + image.contentType + ";base64," + imageBuffer
              }))
            )
          });
          // Replace emoji image tags with alt text (only for single-char emoji alt)
          let htmlWithMeta = '<meta charset="UTF-8">' + htmlResult.value;
          // Only strip inline emoji images (single-codepoint alt)
          htmlWithMeta = htmlWithMeta.replace(/<img[^>]*alt="([^\"]+)"[^>]*>/g, (_match, alt) => {
            return alt.length <= 2 ? alt : _match;
          });
      const turndownService = new TurndownService();
          const markdownText = turndownService.turndown(htmlWithMeta);

          // Log the markdown text before sending to n8n
          console.log(`Markdown for ${file.name} before sending to n8n:`, markdownText);

      const automationData = {
        status: 'Pending',
        deployer: user?.name || 'Unknown',
        date: new Date().toLocaleDateString('en-GB'),
            promptName: promptNameForFile,
            promptText: (useCustomPrompt ? convertPromptContent : prompts.find(p => p.id === convertPromptId)?.text || '')
              + (extendedPromptText ? ' ' + extendedPromptText : ''),
            fileName: file.name.replace(/\.docx$/i, '').trim(),
            createdAt: Date.now(),
            ...(addKnowledge ? { knowledgeUsed: knowledgeFiles.map(f => f.name) } : {}),
      };

      const automationRef = await addDoc(collection(dbFiles, 'automations'), automationData);
          currentAutomationId = automationRef.id;

          const payload: { autoID: string; markdown: string; prompt: string; knowledge?: string } = {
            autoID: currentAutomationId,
            markdown: markdownText,
            prompt: convertPromptContent + (extendedPromptText ? ' ' + extendedPromptText : ''),
          };

          if (knowledgeContextString) {
            payload.knowledge = knowledgeContextString;
          }

          // Log the payload before sending
          console.log('Sending conversion payload for', file.name, payload);
      const response = await fetch('https://n8n-mhi3.onrender.com/webhook/convert-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_BEARER_TOKEN}` },
        body: JSON.stringify(payload),
      });

          // Log the response status and try to log the response body
          let responseText = '';
          try {
            responseText = await response.text();
          } catch (e) {
            responseText = '[Could not read response body]';
          }
          console.log('Conversion response for', file.name, 'status:', response.status, 'body:', responseText);

          if (!response.ok) {
            if (currentAutomationId) {
              await deleteDoc(doc(dbFiles, 'automations', currentAutomationId));
            }
            // Log the error details
            console.error('Conversion request failed for', file.name, 'status:', response.status, 'body:', responseText);
            throw new Error(`Conversion request failed for ${file.name}`);
          }
        } catch (fileError) {
          console.error(`Error processing ${file.name}:`, fileError);
          showNotification(`Failed to create automation for ${file.name}!`, 'error');
          allSuccessful = false;
          if (currentAutomationId) { // Attempt to delete if record was created before error
            try {
              await deleteDoc(doc(dbFiles, 'automations', currentAutomationId));
            } catch (deleteError) {
              console.error(`Failed to cleanup automation record ${currentAutomationId}:`, deleteError);
            }
          }
          // Optionally, decide if you want to stop processing further files on first error
          // break; 
        }
      }

      if (allSuccessful) {
        showNotification('All automations created successfully!', 'success');
      } else {
        showNotification('Some automations failed. Please check the list.', 'warning');
      }
      setShowConvertPopup(false);
      fetchAutomations();

    } catch (error) { // Catch errors outside the loop (e.g., initial setup)
      console.error(error);
      showNotification('An unexpected error occurred while creating automations!', 'error');
      allSuccessful = false; // Should already be handled by inner try/catch
    } finally {
      hideLoader();
      setIsPayloadSent(false);
      setConvertFiles([]); // Clear selected files
      setKnowledgeFiles([]); // Clear knowledge files
      setAddKnowledge(false); // Reset knowledge checkbox
    }
  };

  // Filter prompts when a category is selected or prompts/categories data changes
  useEffect(() => {
    if (selectedPromptCategory) {
      if (selectedPromptCategory === 'Uncategorized') {
        setFilteredPrompts(prompts.filter(p => !p.category || p.category === 'Uncategorized'));
      } else {
        setFilteredPrompts(prompts.filter(p => p.category === selectedPromptCategory));
      }
    } else {
      // If no category selected, or on initial load, show all prompts or handle as needed
      // For now, let's keep it empty until a category is chosen, or show all if desired
      setFilteredPrompts([]); // Or setPrompts(prompts) to show all initially
    }
  }, [selectedPromptCategory, prompts, promptCategories]);

  // Generate and prompt download of DOCX file from formatted Markdown
  const downloadDocx = async () => {
    // Convert Markdown to HTML
    const html = marked(convertedMarkdown);
    // Wrap with doctype to ensure proper formatting
    const fullHtml = '<!DOCTYPE html>' + html;
    // Convert HTML to DOCX blob
    const blob = htmlDocx.asBlob(fullHtml);
    // Trigger download
    saveAs(blob, 'converted.docx');
  };

  // Download handler: fetch stored TXT, convert to HTML then DOCX, trigger download
  const handleDownload = async (automation: Automation) => {
    showLoader();
    try {
      // Download by storage filename (id.docx)
      const functionUrl = `https://us-central1-dream-lead.cloudfunctions.net/downloadAutomation?id=${automation.id}.docx&token=${process.env.REACT_APP_SECURE_TOKEN}`;
      const response = await fetch(functionUrl);
      if (!response.ok) throw new Error('Failed to download file');
      const blob = await response.blob();
      // Append prompt name (or 'Custom Prompt') to the download filename
      const downloadFileName = `${automation.fileName} - ${automation.promptName}.docx`;
      saveAs(blob, downloadFileName);
    } catch (error) {
      console.error('Download error:', error);
      showNotification('Failed to download file', 'error');
    } finally {
      hideLoader();
    }
  };

  // Handler to delete an automation
  const handleDeleteAutomation = async (id: string) => {
    showLoader();
    try {
      // Call the new Cloud Function to delete automation (record and file)
      const functionUrl = `https://us-central1-dream-lead.cloudfunctions.net/deleteAutomation`; // Or your specific region
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SECURE_TOKEN}`
        },
        body: JSON.stringify({ id })
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to delete automation: ${errorData}`);
      }

      setAutomations(prev => prev.filter(a => a.id !== id));
      showNotification('Automation deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting automation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete automation';
      showNotification(errorMessage, 'error');
    } finally {
      hideLoader();
    }
  };

  // Handler for mass download
  const handleMassDownload = async () => {
    showLoader();
    try {
      if (selectedIds.length === 1) {
        const automation = automations.find(a => a.id === selectedIds[0]);
        if (automation) await handleDownload(automation);
      } else if (selectedIds.length > 1) {
        const zip = new JSZip();
        for (const id of selectedIds) {
          const automation = automations.find(a => a.id === id);
          if (!automation) continue;
          const functionUrl = `https://us-central1-dream-lead.cloudfunctions.net/downloadAutomation?id=${id}.docx&token=${process.env.REACT_APP_SECURE_TOKEN}`;
          const response = await fetch(functionUrl);
          if (!response.ok) continue;
          const blob = await response.blob();
          const filename = `${automation.fileName} - ${automation.promptName}.docx`;
          zip.file(filename, blob);
        }
        const content = await zip.generateAsync({ type: 'blob' });
        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = now.getFullYear();
        const hh = String(now.getHours()).padStart(2, '0');
        const mi = String(now.getMinutes()).padStart(2, '0');
        const zipName = `downloaded-files-${dd}_${mm}_${yyyy}-${hh}_${mi}.zip`;
        saveAs(content, zipName);
      }
    } catch (error) {
      console.error('Mass download error:', error);
      showNotification('Failed to download selected files', 'error');
    } finally {
      hideLoader();
      // Optionally clear selection
      // setSelectedIds([]);
    }
  };

  // Poll pending/processing automations every 2 seconds and update if status changes
  useEffect(() => {
    // Only poll while there are Pending or Processing automations
    if (!automations.some(a => a.status === 'Pending' || a.status === 'Processing')) {
      return;
    }
    const interval = setInterval(async () => {
      try {
        const snapshot = await getDocs(collection(dbFiles, 'automations'));
        const latest = snapshot.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Automation, 'id'>) }));
        setAutomations(prev =>
          prev.map(a => {
            if (a.status === 'Pending' || a.status === 'Processing') {
              const updated = latest.find(l => l.id === a.id);
              if (updated && updated.status !== a.status) {
                return updated;
              }
            }
            return a;
          })
        );
      } catch (error) {
        console.error('Error polling automations:', error);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [automations]);

  const handlePromptMouseEnter = (id: string, target: HTMLElement) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredPromptId(id);
    setTooltipTarget(target);
  };

  const handlePromptMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredPromptId(null);
      setTooltipTarget(null);
    }, 200);
  };

  // Keep tooltip open when hovering over it
  const handleTooltipMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  // Hide tooltip when leaving it
  const handleTooltipMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredPromptId(null);
      setTooltipTarget(null);
    }, 200);
  };

  // Drag & Drop handlers for .doc/.docx files
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };
  const handleConvertDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => /\.(docx?)$/i.test(f.name));
    setConvertFiles(prev => {
      const existing = new Set(prev.map(f => f.name));
      const newFiles = files.filter(f => !existing.has(f.name));
      return [...prev, ...newFiles];
    });
  };
  const handleKnowledgeDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => /\.(docx?)$/i.test(f.name));
    setKnowledgeFiles(prev => {
      const existing = new Set(prev.map(f => f.name));
      const newFiles = files.filter(f => !existing.has(f.name));
      return [...prev, ...newFiles];
    });
  };

  // Determine whether we should show tooltip for hovered automation
  const hoveredAutomation = hoveredPromptId ? automations.find(a => a.id === hoveredPromptId) : null;
  const originalHoveredPromptText = hoveredAutomation ? prompts.find(p => p.name === hoveredAutomation.promptName)?.text || '' : '';
  const shouldShowTooltip = Boolean(
    hoveredAutomation &&
    (hoveredAutomation.promptName === 'Custom Prompt' || hoveredAutomation.promptText !== originalHoveredPromptText)
  );

  const handleMassDeleteClick = () => {
    if (selectedIds.length <= 1) {
      const id = selectedIds[0];
      if (id) {
        handleDeleteAutomation(id);
        setSelectedIds([]);
      }
    } else {
      setShowMassDeleteConfirm(true);
    }
  };

  const performMassDelete = async () => {
    showLoader();
    setShowMassDeleteConfirm(false);
    try {
      const url = `https://us-central1-dream-lead.cloudfunctions.net/deleteAutomation`;
      await Promise.all(
        selectedIds.map(id =>
          fetch(url + `?token=${process.env.REACT_APP_SECURE_TOKEN}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SECURE_TOKEN}` },
            body: JSON.stringify({ id }),
          })
        )
      );
      setAutomations(prev => prev.filter(a => !selectedIds.includes(a.id)));
      showNotification(`${selectedIds.length} automations deleted successfully`, 'success');
      setSelectedIds([]);
    } catch (error) {
      console.error('Mass delete error:', error);
      showNotification('Failed to delete selected automations', 'error');
    } finally {
      hideLoader();
    }
  };

  return (
    <div className="dashpanel">
      <div className="main">
        <div className="head">
          <button className="termsbuttonstyleadd" onClick={() => navigate('/console/automations/prompts')}>
            Prompts
          </button>

          {/* Convert File Popup */}
          {showConvertPopup && (
            <div className="edit-overlay z-ind-25" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
              <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', width: '600px', maxWidth: '90%', boxSizing: 'border-box', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h3 style={{ margin: 0 }}>Create Automation</h3>
                  <button type="button" onClick={() => setShowConvertPopup(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>

                {/* Category and Prompt Selection Area */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    {useCustomPrompt ? 'Custom Prompt' : 'Select Prompt'}
                  </label>

                  {useCustomPrompt ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <textarea
                        value={customPromptText} // Should be convertPromptContent or a new state for custom prompt if it needs to be separate
                        onChange={e => { setCustomPromptText(e.target.value); setConvertPromptContent(e.target.value); }}
                        rows={4}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', marginTop: '0' }}
                        placeholder="Enter your custom prompt here..."
                      />
                      <button
                        type="button"
                        onClick={() => { setUseCustomPrompt(false); setCustomPromptText(''); setConvertPromptContent(''); setSelectedPromptCategory(''); setConvertPromptId(''); }}
                        style={{ marginLeft: '8px', background: '#e53e3e', color: '#fff', border: 'none', borderRadius: '4px', padding: '0 12px', height: '36px', cursor: 'pointer' }}
                      >
                        -
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {/* Category Selector */}
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                        <select
                          value={selectedPromptCategory}
                          onChange={e => {
                            setSelectedPromptCategory(e.target.value);
                            setConvertPromptId(''); // Reset prompt ID when category changes
                            setConvertPromptContent('');
                          }}
                          style={{ flexGrow: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        >
                          <option value="">-- Select Category --</option>
                          <option value="Uncategorized">Uncategorized</option>
                          {promptCategories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => { setUseCustomPrompt(true); setConvertPromptId(''); setConvertPromptContent(''); setSelectedPromptCategory(''); }}
                          style={{ marginLeft: '8px', background: '#3182ce', color: '#fff', border: 'none', borderRadius: '4px', padding: '0 12px', height: '36px', cursor: 'pointer' }}
                          title="Use Custom Prompt"
                        >
                          +
                        </button>
                      </div>

                      {/* Prompt Selector (filtered by category) */}
                      {selectedPromptCategory && (
                  <select
                    value={convertPromptId}
                    onChange={e => {
                      setConvertPromptId(e.target.value);
                            const sel = filteredPrompts.find(p => p.id === e.target.value);
                      setConvertPromptContent(sel?.text || '');
                    }}
                          style={{ flexGrow: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                          disabled={!selectedPromptCategory} // Disable if no category selected
                  >
                    <option value="">-- Select Prompt --</option>
                          {filteredPrompts.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                      )}
                    </div>
                  )}
                </div>

                {/* Insert Add Info to Prompt button below prompt selection */}
                {convertPromptContent && (
                  <div style={{ marginBottom: '16px' }}>
                    <button
                      type="button"
                      onClick={() => setShowExtendedPromptPopup(true)}
                      style={{ background: '#3182ce', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}
                    >
                      {extendedPromptText ? 'Edit extended prompt' : 'Add info to prompt'}
                    </button>
                    {extendedPromptText && (
                      <button
                        type="button"
                        onClick={() => setShowViewPromptPopup(true)}
                        style={{ marginLeft: '8px', background: '#805ad5', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}
                      >
                        View Full Prompt
                      </button>
                    )}
                  </div>
                )}

                {/* File Input Section */}
                <div style={{ marginBottom: '16px' }} onDragOver={handleDragOver} onDrop={handleConvertDrop}>
                  <label htmlFor="file-upload-input"
                         style={{
                           display: 'inline-block',
                           padding: '10px 15px',
                           cursor: 'pointer',
                           border: '1px solid #ccc',
                           borderRadius: '4px',
                           backgroundColor: '#f0f0f0',
                           marginBottom: '12px',
                           textAlign: 'center',
                           width: '100%',
                           boxSizing: 'border-box',
                           fontWeight: '500'
                         }}>
                    Choose DOC/DOCX File(s) for Automation (or drop files here)
                  </label>
                  <input id="file-upload-input" type="file" accept=".doc,.docx" multiple 
                         onChange={e => {
                           const newFiles = e.target.files ? Array.from(e.target.files) : [];
                           setConvertFiles(prevFiles => {
                             const existingNames = new Set(prevFiles.map(f => f.name));
                             const uniqueNewFiles = newFiles.filter(nf => !existingNames.has(nf.name));
                             return [...prevFiles, ...uniqueNewFiles];
                           });
                           if (e.target) e.target.value = '' ;
                         }} 
                         style={{ display: 'none' }} />

                  {convertFiles.length > 0 && (
                    <div style={{ marginTop: '8px', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '12px', maxHeight: '100px', overflowY: 'auto' }}>
                      <h4 style={{ margin: '0 0 10px 0', fontWeight: '600', fontSize: '0.95em', color: '#333' }}>Selected automation files:</h4>
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {convertFiles.map((file, index) => (
                          <li key={`convert-${file.name}-${index}-${file.lastModified}`} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 4px',
                            fontSize: '0.9em',
                            borderBottom: index < convertFiles.length - 1 ? '1px dashed #e8e8e8' : 'none'
                          }}>
                            <span title={file.name} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginRight: '10px', color: '#555' }}>{file.name}</span>
                            <button type="button" 
                                    onClick={() => setConvertFiles(prevFiles => prevFiles.filter((_, i) => i !== index))}
                                    title="Remove file"
                                    style={{ background: 'transparent', border: 'none', color: '#cc0000', cursor: 'pointer', fontSize: '1.2rem', padding: '0 5px', fontWeight: 'bold' }}>
                              &times;
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Add Knowledge Section */}
                <div style={{ marginBottom: '16px' }} onDragOver={handleDragOver} onDrop={handleKnowledgeDrop}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '12px' }}>
                    <input 
                      type="checkbox" 
                      checked={addKnowledge} 
                      onChange={e => {
                        setAddKnowledge(e.target.checked);
                        if (!e.target.checked) {
                          setKnowledgeFiles([]); // Clear knowledge files when unchecked
                        }
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    Add Knowledge
                  </label>

                  {addKnowledge && (
                    <>
                      <label htmlFor="knowledge-file-upload-input"
                             style={{
                               display: 'inline-block',
                               padding: '10px 15px',
                               cursor: 'pointer',
                               border: '1px solid #ccc',
                               borderRadius: '4px',
                               backgroundColor: '#f0f0f0',
                               marginBottom: '12px',
                               textAlign: 'center',
                               width: '100%',
                               boxSizing: 'border-box',
                               fontWeight: '500'
                             }}>
                        Choose DOC/DOCX File(s) for Knowledge Base (or drop files here)
                      </label>
                      <input id="knowledge-file-upload-input" type="file" accept=".doc,.docx" multiple 
                             onChange={e => {
                               const newFiles = e.target.files ? Array.from(e.target.files) : [];
                               setKnowledgeFiles(prevFiles => {
                                 const existingNames = new Set(prevFiles.map(f => f.name));
                                 const uniqueNewFiles = newFiles.filter(nf => !existingNames.has(nf.name));
                                 return [...prevFiles, ...uniqueNewFiles];
                               });
                               if (e.target) e.target.value = '' ;
                             }} 
                             style={{ display: 'none' }} />

                      {knowledgeFiles.length > 0 && (
                        <div style={{ marginTop: '8px', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '12px', maxHeight: '100px', overflowY: 'auto', marginBottom: '12px' }}>
                          <h4 style={{ margin: '0 0 10px 0', fontWeight: '600', fontSize: '0.95em', color: '#333' }}>Selected knowledge files:</h4>
                          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {knowledgeFiles.map((file, index) => (
                              <li key={`knowledge-${file.name}-${index}-${file.lastModified}`} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 4px',
                                fontSize: '0.9em',
                                borderBottom: index < knowledgeFiles.length - 1 ? '1px dashed #e8e8e8' : 'none'
                              }}>
                                <span title={file.name} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginRight: '10px', color: '#555' }}>{file.name}</span>
                                <button type="button" 
                                        onClick={() => setKnowledgeFiles(prevFiles => prevFiles.filter((_, i) => i !== index))}
                                        title="Remove knowledge file"
                                        style={{ background: 'transparent', border: 'none', color: '#cc0000', cursor: 'pointer', fontSize: '1.2rem', padding: '0 5px', fontWeight: 'bold' }}>
                                  &times;
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Knowledge Prompt Reminder Alert */}
                      <div style={{
                        backgroundColor: '#fffacd', // Lemon Chiffon (light yellow)
                        border: '1px solid #ffd700', // Gold (darker yellow)
                        color: '#8b4513', // SaddleBrown (dark brown for text)
                        padding: '10px 15px',
                        borderRadius: '4px',
                        fontSize: '0.9em'
                      }}>
                        Remember to refer to your knowledge in the prompt.
                      </div>
                    </>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                  {!isPayloadSent ? (
                    <>
                      <button
                        onClick={handleSendConversion}
                        style={{ background: '#38a169', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', marginRight: '8px' }}
                      >
                        Send
                      </button>
                      <button
                        onClick={() => setShowConvertPopup(false)}
                        style={{ background: '#e2e8f0', color: '#2d3748', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="loader" />
                      <span style={{ marginLeft: '8px' }}>Creating automation...</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Insert Extended Prompt Popup */}
          {showExtendedPromptPopup && (
            <div className="edit-overlay z-ind-25" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
              <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', width: '500px', maxWidth: '90%', maxHeight: '80vh', overflowY: 'auto', boxSizing: 'border-box', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h3 style={{ margin: 0 }}>Add Info to Prompt</h3>
                  <button type="button" onClick={() => setShowExtendedPromptPopup(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>
                <div style={{ backgroundColor: '#e0f7fa', border: '1px solid #00acc1', color: '#006064', padding: '10px 15px', borderRadius: '4px', fontSize: '0.9em', marginBottom: '16px' }}>
                  Recommended to refer to knowledge
                </div>
                <button
                  type="button"
                  onClick={() => setExtendedPromptText('Refer to the knowledge provided below when answering')}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    padding: '4px 8px',
                    marginBottom: '16px',
                    cursor: 'pointer'
                  }}
                >
                  Enter Default
                </button>
                <textarea
                  value={extendedPromptText}
                  onChange={e => setExtendedPromptText(e.target.value)}
                  rows={4}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', marginBottom: '16px' }}
                  placeholder="Enter additional info here"
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setExtendedPromptText('')} style={{ marginRight: '8px', background: '#e53e3e', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}>
                    Clear
                  </button>
                  <button type="button" onClick={() => setShowExtendedPromptPopup(false)} style={{ background: '#38a169', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Insert View Full Prompt Popup */}
          {showViewPromptPopup && (
            <div className="edit-overlay z-ind-25" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
              <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', width: '500px', maxWidth: '90%', maxHeight: '80vh', overflowY: 'auto', boxSizing: 'border-box', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0 }}>Full Prompt</h3>
                  <button type="button" onClick={() => setShowViewPromptPopup(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>
                <div style={{ marginBottom: '16px', whiteSpace: 'pre-wrap' }}>
                  {convertPromptContent}
                </div>
                {extendedPromptText && (
                  <div
                    style={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      padding: '8px',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {extendedPromptText}
                  </div>
                )}
              </div>
            </div>
          )}

          <h1>Automations</h1>
          <button
            className="termsbuttonstyleadd"
            style={{ backgroundColor: 'var(--color-default)', color: '#fff', margin: 0}}
            onClick={() => setShowConvertPopup(true)}
          >
            New Automation
          </button>

          {/* Automations Table/List */}
          <div className="automations-table" style={{ marginTop: '20px' }}>
            {isLoadingAutomations ? (
              <p>Loading automations...</p>
            ) : automations.length === 0 ? (
              <p>No automations present. Create one first.</p>
            ) : (
              Object.entries(
                automations.reduce((groups: { [key: string]: Automation[] }, automation) => {
                  const date = automation.date;
                  if (!groups[date]) {
                    groups[date] = [];
                  }
                  groups[date].push(automation);
                  return groups;
                }, {})
              ).map(([date, dateAutomations]: [string, Automation[]]) => (
                <div key={date} className="date-group" style={{ marginBottom: '20px' }}>
                  <h3 style={{ borderBottom: '2px solid #eee', paddingBottom: '10px', marginBottom: '15px' }}>{date}</h3>
                  {dateAutomations.map(automation => {
              // Get status-specific description
              const getStatusDescription = (status: string) => {
                switch (status) {
                  case 'Pending':
                    return 'NAI accepted, waiting for CHATGPT to process';
                  case 'Processing':
                    return 'Currently being processed by the automation system';
                  case 'Completed':
                    return 'Successfully completed and ready for download';
                  case 'Failed':
                    return 'Failed to process. Please try again';
                  default:
                    return '';
                }
              };

              return (
                <div
                  key={automation.id}
                  className={`automation-card${selectedIds.includes(automation.id) ? ' selected' : ''}`}
                  onClick={() => toggleSelection(automation.id)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedIds.includes(automation.id) ? '#fff1fe' : '#fff',
                    borderRadius: '15px',
                    padding: '4px',
                    marginBottom: '10px',
                    boxShadow: selectedIds.includes(automation.id)
                      ? '0 4px 8px rgba(0,0,0,0.15)'
                      : '0 2px 4px rgba(0,0,0,0.1)',
                    transform: selectedIds.includes(automation.id) ? 'scale(1.001)' : 'scale(1)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border 0.2s ease, background-color 0.2s ease',
                    position: 'relative',
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '8px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <th style={{ width: '200px', textAlign: 'left', padding: '8px 10px', color: '#666', fontSize: '0.9em', fontWeight: 'bold' }}>Document Name</th>
                        <th style={{ width: '150px', textAlign: 'left', padding: '8px 10px', color: '#666', fontSize: '0.9em', fontWeight: 'bold' }}>Prompt</th>
                        <th style={{ width: '100px', textAlign: 'left', padding: '8px 10px', color: '#666', fontSize: '0.9em', fontWeight: 'bold' }}>Status</th>
                        <th style={{ width: '100px', textAlign: 'left', padding: '8px 10px', color: '#666', fontSize: '0.9em', fontWeight: 'bold' }}>Deployer</th>
                        <th style={{ width: '100px', textAlign: 'left', padding: '8px 10px', color: '#666', fontSize: '0.9em', fontWeight: 'bold' }}>Date</th>
                        <th style={{ textAlign: 'left', padding: '8px 10px', color: '#666', fontSize: '0.9em', fontWeight: 'bold' }}>Description</th>
                              <th style={{ textAlign: 'left', padding: '8px 10px', color: '#666', fontSize: '0.9em', fontWeight: 'bold' }}>Knowledge Used</th>
                        <th style={{ width: '100px', textAlign: 'center', padding: '8px 10px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ padding: '8px 10px' }}><h3 style={{ margin: 0 }}>{automation.fileName.replace(/\.docx$/i, '')}</h3></td>
                              <td style={{ padding: '8px 10px', color: '#666', fontSize: '0.9em' }}>
                                <div style={{ position: 'relative', display: 'inline-block' }}
                                     onMouseEnter={e => handlePromptMouseEnter(automation.id, e.currentTarget as HTMLElement)}
                                     onMouseLeave={handlePromptMouseLeave}>
                                  <span style={{
                                    cursor: 'pointer',
                                    textDecoration: 'underline dotted #4c1cbd',
                                    animation: 'pulse 2s infinite',
                                    color: 'magenta'
                                  }}>
                                    {automation.promptName}
                                  </span>
                                </div>
                              </td>
                        <td style={{ padding: '8px 10px', color: '#666', fontSize: '0.9em' }}>
                          <span style={{
                            color: automation.status === 'Completed' ? '#48BB78' :
                                   automation.status === 'Failed' ? '#F56565' :
                                   automation.status === 'Processing' ? '#4299E1' : '#F6AD55'
                          }}>{automation.status}</span>
                        </td>
                        <td style={{ padding: '8px 10px', color: '#666', fontSize: '0.9em' }}>{automation.deployer}</td>
                        <td style={{ padding: '8px 10px', color: '#666', fontSize: '0.9em' }}>{automation.date}</td>
                        <td style={{ padding: '8px 10px', color: '#666', fontSize: '0.9em' }}>{getStatusDescription(automation.status)}</td>
                              <td style={{ padding: '8px 10px', color: '#666', fontSize: '0.9em' }}> 
                                {automation.knowledgeUsed?.length ? automation.knowledgeUsed.join(', ') : '-'}
                              </td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                          {automation.status === 'Completed' ? (
                                        <>
                            <button
                              className="sitemanagerbutton addhref chunkpadded"
                              onClick={(e) => { e.stopPropagation(); handleDownload(automation); }}
                              disabled={selectedIds.length > 0}
                            >
                              Download
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteAutomation(automation.id); }}
                              style={{ marginLeft: '8px', background: '#e53e3e', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer' }}
                              disabled={selectedIds.length > 0}
                            >
                              Delete
                            </button>
                                        </>
                          ) : (
                            <FontAwesomeIcon
                              icon={['fas', 'spinner']}
                              spin
                              style={{
                                color: 'var(--color-default)',
                                fontSize: '2.2em'
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
              ))
            )}
          </div>
        </div>
      </div>
      {tooltipTarget && createPortal(
        <div
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          style={{
            position: 'absolute',
            top: `${tooltipTarget.getBoundingClientRect().bottom + window.scrollY}px`,
            left: `${tooltipTarget.getBoundingClientRect().left + window.scrollX}px`,
            marginTop: '8px',
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            padding: '8px',
            width: '300px',
            zIndex: 100001,
            whiteSpace: 'pre-wrap',
            maxHeight: '50vh',
            overflowY: 'auto',
          }}>
          <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '8px' }}>
            {automations.find(a => a.id === hoveredPromptId)?.promptText}
          </div>
          <button
            onClick={() => { const text = automations.find(a => a.id === hoveredPromptId)?.promptText || ''; navigator.clipboard.writeText(text); showNotification('Custom Prompt Copied', 'success'); }}
            style={{ background: '#3182ce', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer' }}
          >
            Copy
          </button>
        </div>,
        document.body
      )}
      {/* Mass Delete Confirmation Modal */}
      {showMassDeleteConfirm && (
        <div className="edit-overlay z-ind-25" style={{ position:'fixed', top:0,left:0,width:'100%',height:'100%', background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
          <div style={{ background:'#fff', padding:'24px', borderRadius:'8px', width:'90%', maxWidth:'400px', textAlign:'center', boxShadow:'0 4px 12px rgba(0,0,0,0.15)' }}>
            <p style={{ margin:0, fontSize:'1em' }}>You are about to delete {selectedIds.length} files. Would you like to proceed?</p>
            <div style={{ marginTop:'16px', display:'flex', justifyContent:'center' }}>
              <button onClick={performMassDelete} style={{ marginRight:'8px', padding:'8px 16px', background:'#38a169', color:'#fff', border:'none', borderRadius:'4px', cursor:'pointer' }}>
                Proceed
              </button>
              <button onClick={() => setShowMassDeleteConfirm(false)} style={{ padding:'8px 16px', background:'#e53e3e', color:'#fff', border:'none', borderRadius:'4px', cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Mass action bottom bar */}
      <div style={{
        position: 'fixed',
        bottom: selectedIds.length > 0 ? '0px' : '-70px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '800px',
        height: '60px',
        backgroundColor: 'var(--primary-action-color)',
        color: '#fff',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        transition: 'bottom 0.3s ease',
        zIndex: 1000,
        borderRadius: '8px 8px 0 0',
      }}>
        <span style={{ fontWeight: '600' }}>{selectedIds.length} selected</span>
        <div>
          <button onClick={handleMassDeleteClick} disabled={selectedIds.length === 0} style={{ marginRight: '12px', padding: '8px 20px', background: '#e53e3e', color: '#fff', border: 'none', borderRadius: '4px', cursor: selectedIds.length ? 'pointer' : 'not-allowed', opacity: selectedIds.length ? 1 : 0.6, fontWeight: '600' }}>
            Delete
          </button>
          <button
            onClick={handleMassDownload}
            style={{ padding: '8px 20px', background: '#4c51bf', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
          >
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutomationsPage; 